import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDropDown, ArrowDropUp, Close, Fullscreen, GeneratingTokens } from '@mui/icons-material';
import { addDoc, collection, doc, increment, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../utils/firebase';
import GenerateImageInfo from './GenerateImageInfo';
import GeneratedImagesMasonry from './GeneratedImagesMasonry';
import SEO from '../../config/SEO';
import { useUser } from '../../context/UserContext';
import { Link } from 'react-router-dom';

const LOCAL_API_KEY = process.env.REACT_APP_LOCAL_API_KEY;
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

const MODEL_OPTIONS = [
     { label: 'Flux Pro', value: 'flux' },
     { label: 'Turbo', value: 'z-image-turbo' },
];

const DEFAULT_FORM = {
     prompt: '',
     model: 'flux',
     seed: '-1',
     width: '1024',
     height: '1024',
     enhance: false,
     visibility: 'public',

};


const GenerateImage = () => {

     const { userData, isPremium } = useUser();
     const isLoggedIn = !!userData?.uid;
     const [formData, setFormData] = useState(DEFAULT_FORM);
     const [imageUrl, setImageUrl] = useState('');
     const [lastPayload, setLastPayload] = useState(null);
     const [loading, setLoading] = useState(false);
     const [error, setError] = useState('');
     const [isPreviewOpen, setIsPreviewOpen] = useState(true);
     const [showPreview, setShowPreview] = useState(true);


     const normalizeDimension = (value, fallback) => {
          const cleanValue = String(value).replace(/[^\d]/g, '');
          if (!cleanValue) return fallback;

          const numberValue = Math.max(256, Math.min(2048, Number(cleanValue)));
          return String(numberValue);
     };


     const isGenerateDisabled = useMemo(() => {
          return !formData.prompt.trim() || !formData.width || !formData.height;
     }, [formData]);


     const calculateImageCost = (width, height) => {
          const safeWidth = Number(width) || 0;
          const safeHeight = Number(height) || 0;
          const totalPixels = safeWidth * safeHeight;

          if (totalPixels <= 512 * 512) return 20;
          if (totalPixels <= 1024 * 1024) return 30;
          if (totalPixels <= 1024 * 1536) return 40;
          if (totalPixels <= 1536 * 1536) return 50;
          return 60;
     };


     const currentImageCost = useMemo(() => {
          const width = normalizeDimension(formData.width, '1024');
          const height = normalizeDimension(formData.height, '1024');
          return calculateImageCost(width, height);
     }, [formData.width, formData.height]);

     useEffect(() => {
          return () => {
               if (imageUrl && imageUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(imageUrl);
               }
          };
     }, [imageUrl]);

     const handleChange = (field, value) => {
          setFormData((prev) => ({
               ...prev,
               [field]: value,
          }));
     };

     const generateRandomSeed = () => {
          const randomSeed = Math.floor(Math.random() * 1000000000).toString();
          handleChange('seed', randomSeed);
     };



     const resolveSeed = (seedValue) => {
          const trimmed = String(seedValue || '').trim();

          if (!trimmed || trimmed === '-1') {
               return Math.floor(Math.random() * 1000000000).toString();
          }

          return trimmed;
     };

     const buildImageUrl = (payload) => {
          const prompt = encodeURIComponent(payload.prompt.trim());

          const params = new URLSearchParams({
               model: payload.model,
               width: payload.width,
               height: payload.height,
               seed: payload.seed,
               enhance: String(payload.enhance),
               key: LOCAL_API_KEY,
          });

          return `https://gen.pollinations.ai/image/${prompt}?${params.toString()}`;
     };

     const uploadToCloudinary = async (fileOrBlob) => {
          const uploadFormData = new FormData();
          uploadFormData.append('file', fileOrBlob);
          uploadFormData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);

          const response = await fetch(
               `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
               {
                    method: 'POST',
                    body: uploadFormData,
               }
          );

          const data = await response.json();

          if (!response.ok || !data.secure_url) {
               throw new Error(data?.error?.message || 'Cloudinary upload failed.');
          }

          return data;
     };

     const saveGenerationToFirestore = async ({ payload, imageurl, imageCost }) => {
          const auth = getAuth();
          const currentUser = auth.currentUser;

          if (!currentUser) {
               throw new Error('Please login first.');
          }

          await addDoc(collection(db, 'generations'), {
               userID: userData?.uid,
               username: userData?.name || userData?.email || userData?.username || 'Unknown User',
               prompt: payload.prompt,
               modal: payload.model,
               seed: payload.seed,
               height: payload.height,
               width: payload.width,
               time: serverTimestamp(),
               imageurl,
               generatecost: `-${imageCost}`,
               status: 'success',
               visibility: payload.visibility || 'public',
          });
     };

     const handleGenerate = async (customPayload = null) => {


          const source = customPayload || formData;

          const payload = {
               ...source,
               width: normalizeDimension(source.width, '1024'),
               height: normalizeDimension(source.height, '1024'),
               seed: resolveSeed(source.seed),
               visibility: source.visibility || 'public',
          };


          const imageCost = calculateImageCost(payload.width, payload.height);



          if ((userData?.generation_credits || 0) < imageCost) {
               setError(`You need at least ${imageCost} credits to generate this image size.`);
               return;
          }

          if (!payload.prompt.trim()) {
               setError('Please enter an image prompt.');
               return;
          }

          if (!LOCAL_API_KEY || LOCAL_API_KEY === 'PASTE_YOUR_TEST_API_KEY_HERE') {
               setError('Please add your API key in first.');
               return;
          }

          try {
               setLoading(true);
               setError('');

               if (imageUrl && imageUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(imageUrl);
               }

               const finalUrl = buildImageUrl(payload);

               const response = await fetch(finalUrl);
               if (!response.ok) {
                    throw new Error('Failed to generate image.');
               }

               const blob = await response.blob();

               if (!blob || !blob.size) {
                    throw new Error('Generated image is empty.');
               }

               const uploaded = await uploadToCloudinary(blob);
               const permanentUrl = uploaded.secure_url;

               await saveGenerationToFirestore({
                    payload,
                    imageurl: permanentUrl,
                    imageCost,
               });

               await updateDoc(doc(db, 'users', userData.uid), {
                    generation_credits: increment(-imageCost),

               });

               setImageUrl(permanentUrl);
               setLastPayload(payload);
               setIsPreviewOpen(true);
               setShowPreview(true);

               setFormData((prev) => ({
                    ...prev,
                    prompt: '',
                    width: payload.width,
                    height: payload.height,
                    seed: payload.seed,
                    visibility: payload.visibility,
               }));
          } catch (err) {
               setError(err.message || 'Something went wrong while generating the image.');
          } finally {
               setLoading(false);
          }
     };

     const handleLastGenerate = () => {
          if (!lastPayload) return;
          handleGenerate(lastPayload);
     };

     const handleSubmit = (e) => {
          e.preventDefault();
          handleGenerate();
     };


     const notEnoughCredits = (userData?.generation_credits || 0) < currentImageCost;


     return (

          <>

               <SEO
                    title="Generate AI Images Free Online | Free AI Image Generator - HeyPrompt"
                    description="Generate AI images free online with HeyPrompt. Turn text prompts into high-quality AI art, realistic images, digital designs, marketing creatives and social media visuals with our free AI image generator."
                    keywords="generate ai images free, free ai image generator online, generate image ai free, ai image generator free online, free image generate tool, ai art generator, text to image generator, ai image maker, ai image creator free, prompt based image generator, generate ai art free, free ai image prompt tool, online image generation ai, ai visual generator, heyprompt"
                    canonicalPath="/generate-image"
               />


               <section className="generate-image-section">
                    <div className="generate-image-wrap">
                         <div className="generate-image-head">
                              <div className="title-box">
                                   <span className="mini-badge">AI Image Studio</span>
                                   <h2>Free AI Image Generator - Create Realistic Images Instantly</h2>
                                   <p>
                                        This is an AI Image Generator. It creates an image from scratch from a
                                        text description.
                                   </p>

                              </div>

                              <div className="head-actions">
                                   <button
                                        type="button"
                                        className="secondary-btn"
                                        onClick={handleLastGenerate}
                                        disabled={!lastPayload || loading}
                                   >
                                        Last Generate Now
                                   </button>

                                   <button
                                        type="submit"
                                        form="generate-image-form"
                                        className="primary-btn"
                                        disabled={isGenerateDisabled || loading}
                                   >
                                        {loading ? 'Generating...' : 'Generate Now'}
                                   </button>
                              </div>
                         </div>

                         <div className="generate-image-grid">
                              <div className="row">
                                   <div className="col-12">
                                        <div className="generator-panel">
                                             <form id="generate-image-form" onSubmit={handleSubmit}>
                                                  <div className="field-group full">
                                                       <label>Prompt</label>
                                                       <textarea
                                                            placeholder="A cinematic portrait of a futuristic woman walking in neon rain, ultra detailed..."
                                                            value={formData.prompt}
                                                            onChange={(e) =>
                                                                 handleChange('prompt', e.target.value)
                                                            }
                                                       />
                                                  </div>

                                                  <div className="field-row two">

                                                       <div className="field-group">
                                                            <label>Prompt Visibility</label>
                                                            <select value={formData.visibility} onChange={(e) => handleChange('visibility', e.target.value)}>
                                                                 <option value="" disabled>Select visibility</option>
                                                                 <option value="public">Public</option>
                                                                 <option value="private">Private</option>
                                                                 <option disabled={!isPremium} value="subscribers">Subscribers</option>
                                                            </select>
                                                       </div>

                                                       <div className="field-group">
                                                            <label>Model</label>
                                                            <select
                                                                 value={formData.model}
                                                                 onChange={(e) =>
                                                                      handleChange('model', e.target.value)
                                                                 }
                                                            >
                                                                 {MODEL_OPTIONS.map((item) => (
                                                                      <option
                                                                           key={item.value}
                                                                           value={item.value}
                                                                      >
                                                                           {item.label}
                                                                      </option>
                                                                 ))}
                                                            </select>
                                                       </div>





                                                       <div className="field-group">
                                                            <label>Width</label>
                                                            <input
                                                                 className='input-number'
                                                                 type="number"
                                                                 value={formData.width}
                                                                 onChange={(e) =>
                                                                      handleChange('width', e.target.value)
                                                                 }
                                                            />
                                                       </div>

                                                       <div className="field-group">
                                                            <label>Height</label>
                                                            <input
                                                                 type="number"
                                                                 className='input-number'
                                                                 value={formData.height}
                                                                 onChange={(e) =>
                                                                      handleChange('height', e.target.value)
                                                                 }
                                                            />
                                                       </div>

                                                       <div className="field-group">
                                                            <label>Seed</label>
                                                            <div className="inline-field">
                                                                 <input
                                                                      type="text"
                                                                      placeholder="-1"
                                                                      value={formData.seed}
                                                                      onChange={(e) =>
                                                                           handleChange(
                                                                                'seed',
                                                                                e.target.value
                                                                           )
                                                                      }
                                                                 />
                                                                 <button
                                                                      type="button"
                                                                      className="ghost-btn"
                                                                      onClick={generateRandomSeed}
                                                                 >
                                                                      Random
                                                                 </button>
                                                            </div>
                                                       </div>

                                                       <div className="field-row single action-row">

                                                            {!isLoggedIn ?
                                                                 <Link to="/login" className="primary-btn">
                                                                      Login Now
                                                                 </Link>
                                                                 :
                                                                 <button
                                                                      type="submit"
                                                                      className="primary-btn"
                                                                      disabled={!isLoggedIn || isGenerateDisabled || loading || notEnoughCredits}
                                                                 >
                                                                      <GeneratingTokens />
                                                                      {notEnoughCredits
                                                                           ? `Need ${currentImageCost} Credits`
                                                                           : loading
                                                                                ? 'Generating...'
                                                                                : `${currentImageCost} Generate Now`}
                                                                 </button>
                                                            }
                                                       </div>
                                                  </div>

                                                  {error ? (
                                                       <div className="status-message error">{error}</div>
                                                  ) : null}
                                             </form>
                                        </div>
                                   </div>

                                   {!isLoggedIn ? (
                                        <div className="login-warning text-danger">
                                             Log in to generate AI images, save your creations, and access all features.
                                        </div>
                                   ) : (
                                        (() => {
                                             const credits = userData?.generation_credits || 0;

                                             if (credits < currentImageCost) {
                                                  return (
                                                       <div className="login-warning text-danger">
                                                            You have <strong>{credits} credits</strong>, but this image requires <strong>{currentImageCost} credits</strong>.
                                                            <Link to="/pricing?credits" className="buy-link"> Buy Credits </Link>
                                                       </div>
                                                  );
                                             }

                                             return (
                                                  <div className="login-warning text-center">
                                                       You have <strong>{credits} credits</strong>. This image will cost <strong>{currentImageCost} credits</strong>.
                                                  </div>
                                             );
                                        })()
                                   )}
                              </div>

                              {imageUrl && showPreview ? (
                                   <div className="preview-panel">
                                        <div
                                             className="preview-top"
                                             onClick={() => setIsPreviewOpen(!isPreviewOpen)}
                                        >
                                             <div>
                                                  <span className="preview-label">Live Preview</span>
                                                  <h3>Generated Output</h3>
                                             </div>

                                             <div className="link-supes">
                                                  {isPreviewOpen ? <ArrowDropUp /> : <ArrowDropDown />}

                                                  <button
                                                       type="button"
                                                       className="close-preview-btn"
                                                       onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowPreview(false);
                                                       }}
                                                  >
                                                       <Close />
                                                  </button>
                                             </div>
                                        </div>

                                        {isPreviewOpen && (
                                             <div className="preview-box">

                                                  {imageUrl ? (
                                                       <img src={imageUrl} alt="Generated preview" />
                                                  ) : (
                                                       <div className="empty-preview">
                                                            <span>Preview will appear here after generation</span>
                                                       </div>
                                                  )}

                                                  {loading && (
                                                       <div className="__loading-sc">
                                                            <div className="loader"></div>
                                                       </div>
                                                  )}

                                                  {/* ACTIONS */}
                                                  {imageUrl && (
                                                       <div className="link-image-upp-now">
                                                            <Link to={imageUrl}
                                                                 target='_blank'
                                                                 className="download-btn">
                                                                 <Fullscreen />
                                                            </Link>
                                                       </div>
                                                  )}
                                             </div>
                                        )}
                                   </div>
                              ) : null}
                         </div>
                    </div>

                    <div className="mt-5">
                         <GeneratedImagesMasonry
                              userData={userData}
                         />
                    </div>

                    <GenerateImageInfo />
               </section>

          </>

     );
};

export default GenerateImage;